import { clsx } from '@alexis/helpers/clsx';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { getIsRtlState } from '@redux/selectors';

import styles from './LegDetailsCarouselContainer.module.scss';

type LegDetailsCarouselContainerProps = {
  activeLeg: number;
  children: React.ReactNode;
  itemWidth: number;
  numberOfLegs: number;
  handleLeftChevronClick: () => void;
  handleRightChevronClick: () => void;
  setItemWidth: (itemWidth: number) => void;
};

const LegDetailsCarouselContainer: React.FC<LegDetailsCarouselContainerProps> = ({
  activeLeg,
  children,
  itemWidth,
  numberOfLegs,
  handleLeftChevronClick,
  handleRightChevronClick,
  setItemWidth,
}) => {
  const isRtl = useSelector(getIsRtlState);

  const containerDivRef = useRef<HTMLDivElement>(null);
  const [maxScrollLeft, setMaxScrollLeft] = useState<number>(0);
  const [translateXPosition, setTranslateXPosition] = useState<number>(0);
  const [isItemOverflowed, setIsItemOverflowed] = useState<boolean>(false);

  // Number of items on the carousel which is the number of legs
  const itemLength = useMemo<number>(() => {
    return numberOfLegs === 1 ? 2 : numberOfLegs; //if there is only one leg, need to show an empty card
  }, [numberOfLegs]);

  // Update the scroll position of the itinerary whenever the active leg is changed
  useEffect(() => {
    if (activeLeg === 0) {
      setTranslateXPosition(0);
    } else if (activeLeg === numberOfLegs - 1) {
      setTranslateXPosition(maxScrollLeft);
    } else {
      const scrollLength = (isRtl ? 1 : -1) * (activeLeg * (itemWidth + 8) - 164);

      const totalLength = numberOfLegs * 320 + (numberOfLegs - 1) * 8 - Math.abs(scrollLength);

      if (!!containerDivRef.current && totalLength < containerDivRef.current.clientWidth) {
        setTranslateXPosition(isRtl ? maxScrollLeft - 16 : maxScrollLeft + 16);
      } else {
        setTranslateXPosition(scrollLength);
      }
    }
  }, [isRtl, activeLeg, numberOfLegs, maxScrollLeft, containerDivRef]);

  useEffect(() => {
    if (containerDivRef.current) {
      if (numberOfLegs <= 2) {
        setItemWidth(Math.floor(containerDivRef.current.clientWidth / 2) - 4); // 4px is half of the gap between the cards
      } else {
        setItemWidth(320); // card size for multicity search is 320px
      }
    }
  }, [containerDivRef, numberOfLegs, activeLeg]);

  // Sets the attributes for the carousel
  useEffect(() => {
    const initializeScrollingValues = (): void => {
      if (containerDivRef.current) {
        const scrollWidth = itemLength * itemWidth + (itemLength - 1) * 8;

        setIsItemOverflowed(scrollWidth > containerDivRef.current.clientWidth);

        setMaxScrollLeft((scrollWidth - containerDivRef.current.clientWidth) * (isRtl ? 1 : -1));

        if (numberOfLegs <= 2) {
          setItemWidth(Math.floor(containerDivRef.current.clientWidth / 2) - 4);
        }
      }
    };

    window.addEventListener('resize', initializeScrollingValues);

    initializeScrollingValues();

    return () => {
      window.removeEventListener('resize', initializeScrollingValues);
    };
  }, [containerDivRef, itemLength, itemWidth, isRtl, numberOfLegs]);

  return (
    <div ref={containerDivRef} className={clsx(styles.carouselContainer, isRtl && styles.rtl)}>
      <div className={styles.scrollPane}>
        <div
          style={{
            transform: `translateX(${translateXPosition}px)`,
            width: `${itemWidth * itemLength + 8 * (itemLength - 1)}px`,
          }}
        >
          {children}
        </div>
      </div>

      {/* Overlay for left shift and right shift buttons carousel */}
      {!(translateXPosition === maxScrollLeft || !isItemOverflowed) ? (
        <div className={clsx(styles.layoverForChevron, isRtl ? styles.left : styles.right)}></div>
      ) : null}

      {!(translateXPosition === 0 || !isItemOverflowed) ? (
        <div className={clsx(styles.layoverForChevron, isRtl ? styles.right : styles.left)}></div>
      ) : null}

      <button
        aria-label='Previous'
        type='button'
        className={styles.left}
        disabled={translateXPosition === 0 || !isItemOverflowed}
        onClick={handleLeftChevronClick}
      >
        <i className={clsx(styles.chevron, isRtl ? styles.right : styles.left)}></i>
      </button>

      <button
        aria-label='Next'
        type='button'
        className={styles.right}
        disabled={translateXPosition === maxScrollLeft || !isItemOverflowed}
        onClick={handleRightChevronClick}
      >
        <i className={clsx(styles.chevron, isRtl ? styles.left : styles.right)}></i>
      </button>
    </div>
  );
};

export default LegDetailsCarouselContainer;
